// src/pages/users/UserCreateEdit.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tab,
  Tabs,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  BusinessCenter as BusinessIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';

// Custom components
import { Card } from '../../design-system';
import TabPanel from '../../components/ui/TabPanel';
import useUsersStore from '../../stores/userStore';
import useAuthStore from '../../stores/authStore';
import useClientsStore from '../../stores/clientStore';
import useRoleStore from '../../stores/roleStore';
import UserRolesPermissions from './UserRolesPermissions';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import SecureAvatar from '../../components/ui/SecureAvatar';

// Group clients in select
const groupClientsByFirstLetter = (clients) => {
  const grouped = {};
  
  clients.forEach(client => {
    const firstLetter = client.name.charAt(0).toUpperCase();
    if (!grouped[firstLetter]) {
      grouped[firstLetter] = [];
    }
    grouped[firstLetter].push(client);
  });
  
  return Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([letter, clients]) => ({
      letter,
      clients: clients.sort((a, b) => a.name.localeCompare(b.name))
    }));
};

const UserCreateEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  // Tabs state
  const [activeTab, setActiveTab] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roleId: '', // Changed from role to roleId
    password: '',
    passwordConfirm: '',
    clientId: '',
    active: true,
    permissions: []
  });
  
  // Form validation
  const [errors, setErrors] = useState({});
  const [dirtyFields, setDirtyFields] = useState({});
  
  // Dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  
  // Stores
  const {
    selectedUser,
    loading,
    error,
    fetchUserById,
    createUser,
    updateUser,
    clearSelectedUser
  } = useUsersStore();
  
  const { user: currentUser, hasPermission } = useAuthStore();
  const { clients, loading: clientsLoading, fetchClients } = useClientsStore();
  const { roles, fetchRoles, loading: rolesLoading } = useRoleStore();
  
  // Load data on mount
  useEffect(() => {
    // Fetch clients for dropdown
    if (clients.length === 0) {
      fetchClients();
    }
    
    // Fetch roles if not loaded
    if (roles.length === 0) {
      fetchRoles();
    }
    
    // If edit mode, fetch user data
    if (isEditMode && id) {
      fetchUserById(id);
    }
    
    // Cleanup on unmount
    return () => {
      clearSelectedUser();
    };
  }, [id, fetchClients, fetchRoles, fetchUserById, clearSelectedUser, clients.length, roles.length, isEditMode]);
  
  // Update form data when user data is loaded
  useEffect(() => {
    if (selectedUser && isEditMode) {
      setFormData({
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        email: selectedUser.email || '',
        phone: selectedUser.phone || '',
        roleId: selectedUser.roleId?._id || selectedUser.roleId || '',
        password: '',
        passwordConfirm: '',
        clientId: selectedUser.clientId?._id || selectedUser.clientId || '',
        active: selectedUser.active !== false,
        permissions: selectedUser.permissions || []
      });
    }
  }, [selectedUser, isEditMode, roles]);
  
  // Handle form input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Mark field as dirty
    setDirtyFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    // If role changes, check if it requires client and clear clientId if not needed
    if (name === 'roleId') {
      const selectedRole = roles.find(r => r._id === value || r.id === value);
      if (selectedRole && !['client-user', 'client-manager', 'client-admin'].includes(selectedRole.key)) {
        setFormData(prev => ({
          ...prev,
          clientId: ''
        }));
      }
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle cancel button
  const handleCancel = () => {
    // Check if form has changes
    if (Object.keys(dirtyFields).length > 0) {
      setCancelDialogOpen(true);
    } else {
      navigate('/users');
    }
  };
  
  // Handle permissions update
  const handlePermissionsUpdate = ({ roleId: newRoleId, permissions: newPermissions }) => {
    setFormData(prev => ({
      ...prev,
      roleId: newRoleId || prev.roleId,
      permissions: newPermissions
    }));
    
    // Mark permissions as dirty
    setDirtyFields(prev => ({
      ...prev,
      roleId: true,
      permissions: true
    }));
    
    // Find the role to check if it's client-related
    const selectedRole = roles.find(r => r._id === newRoleId || r.id === newRoleId);
    if (selectedRole && ['client-user', 'client-manager', 'client-admin'].includes(selectedRole.key) && !formData.clientId) {
      setErrors(prev => ({
        ...prev,
        clientId: 'Client is required for client roles'
      }));
      
      // Switch to clients tab
      setActiveTab(2);
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    // Password validation for new users
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      
      if (!formData.passwordConfirm) {
        newErrors.passwordConfirm = 'Please confirm your password';
      } else if (formData.password !== formData.passwordConfirm) {
        newErrors.passwordConfirm = 'Passwords do not match';
      }
    } else if (formData.password && formData.password.length > 0) {
      // Password validation for existing users (only if provided)
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      
      if (formData.password !== formData.passwordConfirm) {
        newErrors.passwordConfirm = 'Passwords do not match';
      }
    }
    
    // Role validation
    if (!formData.roleId) newErrors.roleId = 'Role is required';
    
    // Client validation - check role key from selected role
    const selectedRole = roles.find(r => r._id === formData.roleId || r.id === formData.roleId);
    if (selectedRole && ['client-user', 'client-manager', 'client-admin'].includes(selectedRole.key) && !formData.clientId) {
      newErrors.clientId = 'Client is required for client roles';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      // Switch to tab with errors
      const hasBasicInfoErrors = ['firstName', 'lastName', 'email', 'phone'].some(field => errors[field]);
      const hasAccountErrors = ['password', 'passwordConfirm', 'roleId'].some(field => errors[field]);
      const hasClientErrors = ['clientId'].some(field => errors[field]);
      
      if (hasBasicInfoErrors) {
        setActiveTab(0);
      } else if (hasAccountErrors) {
        setActiveTab(1);
      } else if (hasClientErrors) {
        setActiveTab(2);
      }
      
      return;
    }
    
    try {
      // Prepare data for API
      const userData = { ...formData };
      
      // Remove passwordConfirm for API
      delete userData.passwordConfirm;
      
      // Remove empty password in edit mode
      if (isEditMode && !userData.password) {
        delete userData.password;
      }
      
      // Format permissions to match API expectations
      if (userData.permissions && userData.permissions.length) {
        // Ensure permissions are in the correct format
        userData.permissions = userData.permissions.map(permission => {
          // Ensure each permission has the required fields
          return {
            resource: permission.resource,
            action: permission.action,
            scope: permission.scope || 'all',
            conditions: permission.conditions || {}
          };
        });
      }
      
      // Set role fields from roleId
      if (userData.roleId) {
        // Find the role object in roles array
        const roleObject = roles.find(r => r._id === userData.roleId || r.id === userData.roleId);
        if (roleObject) {
          // Set roleName for display purposes
          userData.roleName = roleObject.name;
          
          // Remove the deprecated role field - we only use roleId now
          delete userData.role;
        }
      }
      
      if (isEditMode) {
        await updateUser(id, userData);
      } else {
        await createUser(userData);
      }
      
      // Navigate back to users list
      navigate('/users');
    } catch (error) {
      console.log('Error saving user:', error);
      // Handle API errors
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      }
    }
  };
  
  // Get available clients based on user role
  const getAvailableClients = () => {
    if (['superadmin', 'admin', 'agency-admin'].includes(currentUser.role)) {
      return clients;
    }
    
    if (currentUser.assignedClients && currentUser.assignedClients.length > 0) {
      return clients.filter(client => 
        currentUser.assignedClients.includes(client._id)
      );
    }
    
    return [];
  };
  
  // Get available roles based on current user's role
  const getAvailableRoles = () => {
    if (!roles.length) return [];
    
    // Filter roles based on current user's role
    if (currentUser.role === 'superadmin') {
      return roles;
    }
    
    if (currentUser.role === 'admin') {
      return roles.filter(r => r.key !== 'superadmin');
    }
    
    if (currentUser.role === 'agency-admin') {
      return roles.filter(r => 
        ['agency-manager', 'content-creator', 'content-approver', 'client-admin', 'client-manager', 'client-user'].includes(r.key)
      );
    }
    
    if (currentUser.role === 'client-admin') {
      return roles.filter(r => 
        ['client-manager', 'client-user'].includes(r.key)
      );
    }
    
    // Default - restricted roles
    return roles.filter(r => r.key === 'user');
  };
  
  // Group available roles by category
  const groupRolesByCategory = (rolesList) => {
    return rolesList.reduce((acc, role) => {
      const category = role.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(role);
      return acc;
    }, {});
  };
  
  // Group clients for select
  const groupedClients = groupClientsByFirstLetter(getAvailableClients());
  
  // Get available roles and group them
  const availableRoles = getAvailableRoles();
  const groupedRoles = groupRolesByCategory(availableRoles);
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/users')}
          sx={{ cursor: 'pointer' }}
        >
          Users
        </Link>
        <Typography color="text.primary">
          {isEditMode ? 'Edit User' : 'Create User'}
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit User' : 'Create User'}
        </Typography>
        
        <Box>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        <Card level={1}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="user tabs">
              <Tab icon={<AccountIcon />} label="Profile Info" id="user-tab-0" aria-controls="user-tabpanel-0" />
              <Tab icon={<LockIcon />} label="Account" id="user-tab-1" aria-controls="user-tabpanel-1" />
              <Tab icon={<BusinessIcon />} label="Clients" id="user-tab-2" aria-controls="user-tabpanel-2" />
              <Tab icon={<SettingsIcon />} label="Permissions" id="user-tab-3" aria-controls="user-tabpanel-3" />
            </Tabs>
          </Box>
          
          {/* Profile Info Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} lg={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* User Avatar */}
                  <SecureAvatar
                    src={selectedUser?.photo}
                    alt={formData.firstName}
                    sx={{ width: 120, height: 120, mb: 2, fontSize: 48 }}
                  >
                    {formData.firstName.charAt(0)}{formData.lastName.charAt(0)}
                  </SecureAvatar>
                  
                  {/* Photo Upload - Not implemented */}
                  <Button variant="outlined"  disabled>
                    Change Photo
                  </Button>
                  
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Photo upload not implemented in this demo
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={8} lg={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="firstName"
                      label="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      error={Boolean(errors.firstName)}
                      helperText={errors.firstName}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="lastName"
                      label="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      error={Boolean(errors.lastName)}
                      helperText={errors.lastName}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={Boolean(errors.email)}
                      helperText={errors.email}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="phone"
                      label="Phone"
                      value={formData.phone}
                      onChange={handleChange}
                      error={Boolean(errors.phone)}
                      helperText={errors.phone}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Account Tab */}
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={Boolean(errors.roleId)} required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleChange}
                    label="Role"
                    disabled={rolesLoading}
                  >
                    {rolesLoading ? (
                      <MenuItem value="" disabled>Loading roles...</MenuItem>
                    ) : (
                      Object.entries(groupedRoles).map(([category, categoryRoles]) => [
                        <MenuItem key={`group-${category}`} disabled>
                          <Typography variant="caption" color="textSecondary">
                            {category}
                          </Typography>
                        </MenuItem>,
                        ...categoryRoles.map((role) => (
                          <MenuItem key={role._id || role.id} value={role._id || role.id}>
                            {role.name}
                          </MenuItem>
                        ))
                      ]).flat()
                    )}
                  </Select>
                  {errors.roleId && <FormHelperText>{errors.roleId}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                      
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Inactive users cannot log in to the system
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1">Password</Typography>
                {isEditMode && (
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                    Leave these fields empty to keep the current password
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={Boolean(errors.password)}
                  helperText={errors.password || 'Minimum 8 characters'}
                  required={!isEditMode}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="passwordConfirm"
                  label="Confirm Password"
                  type="password"
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  error={Boolean(errors.passwordConfirm)}
                  helperText={errors.passwordConfirm}
                  required={!isEditMode}
                />
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Clients Tab */}
          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={Boolean(errors.clientId)}>
                  <InputLabel>Client</InputLabel>
                  <Select
                    name="clientId"
                    value={formData.clientId}
                    onChange={handleChange}
                    label="Client"
                    disabled={!['client-user', 'client-manager', 'client-admin'].includes(formData.role) || clientsLoading}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    
                    {clientsLoading ? (
                      <MenuItem value="" disabled>Loading clients...</MenuItem>
                    ) : (
                      groupedClients.map(group => [
                        <MenuItem key={`group-${group.letter}`} disabled>
                          <Typography variant="caption" color="textSecondary">
                            {group.letter}
                          </Typography>
                        </MenuItem>,
                        ...group.clients.map(client => (
                          <MenuItem key={client._id} value={client._id}>
                            {client.name}
                          </MenuItem>
                        ))
                      ]).flat()
                    )}
                  </Select>
                  {errors.clientId && <FormHelperText>{errors.clientId}</FormHelperText>}
                  {['client-user', 'client-manager', 'client-admin'].includes(formData.role) && (
                    <FormHelperText>
                      Required for client roles
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {['client-user', 'client-manager', 'client-admin'].includes(formData.role)
                    ? 'Assign this user to a client organization'
                    : 'Client assignment is only required for client roles'}
                </Typography>
              </Grid>
              
              {isEditMode && ['superadmin', 'admin', 'agency-admin'].includes(currentUser.role) && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1">Assigned Clients</Typography>
                    <Typography variant="caption" color="textSecondary">
                      This functionality is not implemented in this demo
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>
          
          {/* Permissions Tab */}
          <TabPanel value={activeTab} index={3}>
            <UserRolesPermissions
              permissions={formData.permissions}
              roleId={formData.roleId}
              onChange={handlePermissionsUpdate}
            />
          </TabPanel>
        </Card>
      </form>
      
      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Discard Changes"
        content="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Continue Editing"
        onConfirm={() => navigate('/users')}
        onCancel={() => setCancelDialogOpen(false)}
      />
    </Box>
  );
};

export default UserCreateEdit;